import type { EstimatedPosition } from '../../lib/closing';
import { formatMoney } from '../../lib/format';

/**
 * The two side-by-side panels and the ROI stat, on the per-investment dashboard.
 *
 * ── THESE PANELS ARE THIS INVESTOR'S OWN NUMBERS, NOT THE DEAL'S ─────────────────────
 *
 * Headed "Your Investment" and "Your Financial Performance" rather than naming the reader
 * an LP or a Member. The entity structure behind it is real (the event SPV LLC is the
 * issuer, Vyrtual LLC is the Manager, investors are Members) but repeating it in every
 * panel heading tells a holder nothing they came here to learn.
 *
 * They used to show POOL figures under a heading that read like the holder's own. Devon,
 * holding 4 of 10 shares, saw "Return of Capital $25,000.00" when his share was $10,000:
 * a true number about the deal, presented as a statement about him. The deal-wide figures
 * now live in Event Financial Performance, above, where they are labelled as such.
 *
 * The tiers are still shown in the order they are paid, which is the order that settles:
 * capital back in full, then the hurdle in full, and only what remains above both is split.
 *
 * THE MANAGER'S PROMOTE IS DELIBERATELY ABSENT. It is the Manager's cut of the excess, not
 * something deducted from this holder's share, and listing it in a panel headed "Your"
 * invites reading it as money taken from them. It stays in the deal-wide block above.
 *
 * ── PARTICIPATION SHARE IS OF THEIR OWN RAISE ────────────────────────────────────────
 *
 * Not of the event's total capital. Where an event carries several raises the pool is split
 * pro-rata and each settles under its own signed terms, so the denominator that governs
 * what this holder is entitled to is their own raise. The event-wide figure is larger and
 * therefore the flattering one, which is exactly why the label says which it is rather than
 * leaving "Participation Share" to be assumed.
 */

function Row({ label, value, muted = false, accent = false }: {
  label: string; value: string; muted?: boolean; accent?: boolean;
}) {
  return (
    <div className="pos-row">
      <dt className={muted ? 'pos-row__label pos-row__label--muted' : 'pos-row__label'}>
        {label}
      </dt>
      <dd className={`pos-row__value mono${accent ? ' pos-row__value--accent' : ''}`}>{value}</dd>
    </div>
  );
}

export function PositionPanels({ position, investedCents, shares }: {
  position: EstimatedPosition;
  investedCents: number;
  shares: number;
}) {
  const b = position.basis;

  // Of their own raise. totalInvestedCapitalCents is this tranche's capital; the ledger
  // also returns eventTotalInvestedCapitalCents, and using that here would overstate the
  // holder's participation whenever the event carries more than one raise.
  const ownRaiseCapital = b.totalInvestedCapitalCents;
  const sharePct = ownRaiseCapital > 0 ? (investedCents / ownRaiseCapital) * 100 : 0;

  const roi = investedCents > 0
    ? ((position.positionCents - investedCents) / investedCents) * 100
    : 0;
  const negative = roi < 0;

  const hurdlePct = (b.preferredReturnRate * 100).toFixed(
    (b.preferredReturnRate * 100) % 1 ? 1 : 0);

  return (
    <>
      <div className="pos-grid">
        <section className="vp-panel">
          <h3 className="vp-gradient-heading pos-title">Your Investment</h3>
          <dl className="pos-list">
            <Row label="Investment Amount" value={formatMoney(investedCents)} />
            <Row
              label="Participation Share (of this raise)"
              value={`${sharePct.toFixed(2)}%`}
            />
            <Row label="Contract Shares" value={shares.toLocaleString('en-US')} />
          </dl>
          <p className="pos-note">
            Share of this raise, which is the figure your contract entitles you to. Where an
            event carries more than one raise, each settles separately under its own terms.
          </p>
        </section>

        <section className="vp-panel">
          <h3 className="vp-gradient-heading pos-title">Your Financial Performance</h3>
          <dl className="pos-list">
            <Row label="Your Return of Capital"
              value={formatMoney(position.returnOfCapitalCents)} />
            <Row label={`Your Preferred Return (${hurdlePct}%)`}
              value={formatMoney(position.preferredReturnCents)} />
            <Row label="Your Share of Profit Above Hurdle"
              value={formatMoney(position.profitShareCents)} />
            <Row label="Your Total Position"
              value={formatMoney(position.positionCents)} accent />
          </dl>
          <p className="pos-note">
            Your proportional share of each tier, paid in this order. Capital is returned in
            full, then the hurdle in full, and only what remains above both is split. The
            deal-wide totals, including the Manager&apos;s promote, are in Event Financial
            Performance above.
          </p>
        </section>
      </div>

      <section className="vp-panel pos-roi">
        <p className="vp-stat__label">ROI prior to final reconciliation</p>
        <p className={`vp-stat__value vp-stat__value--large${negative ? ' vp-stat__value--negative' : ''}`}>
          {negative ? '' : '+'}{roi.toFixed(2)}%
        </p>
        <p className="pos-note pos-note--center">
          An estimate from ticket sales so far, not a settled figure. It moves with every
          sale and refund, and assumes production costs documented to date.
        </p>
      </section>
    </>
  );
}
